import { PROJECT_STATUSES, type ProjectStatus } from '@/shared/constants/statuses';
import type { ProjectStatusCount, StatusCounts } from '@/shared/types/domain';

/**
 * Soma linhas de `project_status_counts` em um mapa situação → total.
 *
 * A view devolve uma linha por `(coordenação, ministério, situação)`; quem
 * decide o recorte é quem chama, passando um filtro. É a mesma consulta
 * servindo a home, as coordenações, a coordenação e o ministério — com ~500
 * projetos/ano, agregar aqui custa menos que uma query por tela (regra 14).
 */
export function aggregateStatusCounts(
  rows: readonly ProjectStatusCount[],
  filter?: (row: ProjectStatusCount) => boolean,
): StatusCounts {
  const counts: StatusCounts = {};
  for (const row of rows) {
    if (filter && !filter(row)) continue;
    counts[row.status] = (counts[row.status] ?? 0) + row.total;
  }
  return counts;
}

export function totalProjects(counts: StatusCounts): number {
  return PROJECT_STATUSES.reduce((total, status) => total + (counts[status] ?? 0), 0);
}

/**
 * Os agrupamentos que a liderança de fato lê (US-10, US-11).
 *
 * "Aguardando decisão" tem UM status desde a ADR-013 — era o grupo que já
 * tratava `submitted` e `under_review` como um número só, porque a diferença
 * entre eles era o passo de kanban do revisor e isso nunca foi assunto de quem
 * olha o panorama de um ministério. O grupo continua existindo, com um status
 * dentro, pelo mesmo motivo de `REVIEW_QUEUE_STATUSES`: o agrupamento é a
 * unidade que a liderança lê, e ela não deve mudar de forma se o enum mudar.
 *
 * `rejected` e `cancelled` não têm grupo: para quem varre um panorama, os dois
 * significam "não vai acontecer", e ocupar espaço com eles empurra para baixo
 * o que exige ação. Continuam acessíveis pelo filtro da listagem.
 */
export const COUNT_GROUPS = [
  { key: 'draft', label: 'Rascunhos', statuses: ['draft'] },
  { key: 'waiting', label: 'Aguardando decisão', statuses: ['under_review'] },
  { key: 'changes', label: 'Com ajustes pedidos', statuses: ['changes_requested'] },
  { key: 'approved', label: 'Aprovados', statuses: ['approved'] },
  { key: 'completed', label: 'Concluídos', statuses: ['completed'] },
] as const satisfies readonly {
  key: string;
  label: string;
  statuses: readonly ProjectStatus[];
}[];

export type CountGroupKey = (typeof COUNT_GROUPS)[number]['key'];

export function groupTotal(counts: StatusCounts, statuses: readonly ProjectStatus[]): number {
  return statuses.reduce((total, status) => total + (counts[status] ?? 0), 0);
}
